import React, { useState, useEffect } from 'react';
import { contactAPI } from '../utils/api';
import { updatePageMeta } from '../utils/seo';
import { metaFor } from '../seo/routeMeta';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from '../utils/phone';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './Contact.css';

function Contact() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  useEffect(() => {
    updatePageMeta(metaFor('/contact'));
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    message: '',
    preferredContact: 'email',
    // Honeypot — hidden from people, filled only by bots. The server drops
    // any submission where it is non-empty.
    website: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get reCAPTCHA token
      let recaptchaToken = '';
      try {
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha('contact');
        }
      } catch (recaptchaErr) {
        console.warn('reCAPTCHA failed, proceeding without:', recaptchaErr);
      }

      await contactAPI.submit({ ...formData, recaptchaToken });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        propertyAddress: '',
        message: '',
        preferredContact: 'email',
        website: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Ready to showcase your property with stunning photography?</p>
        </div>
      </div>

      <div className="container contact-content">
        <div className="contact-grid">
          {/* Contact Form */}
          <div className="contact-form-section">
            <h2>Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                <label htmlFor="website">Leave this field empty</label>
                <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={handleChange} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                    required
                    placeholder="(801) 555-1234"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="preferredContact">Preferred Contact Method</label>
                  <select
                    id="preferredContact"
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleChange}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="text">Text Message</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="propertyAddress">Property Address (Optional)</label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  placeholder="123 Main St, Provo, UT"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Tell us about your photography needs..."
                ></textarea>
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="contact-info-section">
            <div className="info-card">
              <h3>Get in Touch</h3>
              <div className="info-item">
                <i className="icon">📧</i>
                <div>
                  <h4>Email</h4>
                  <p>info@utahrealtyphotos.com</p>
                </div>
              </div>

              <div className="info-item">
                <i className="icon">📱</i>
                <div>
                  <h4>Phone</h4>
                  <p>(801) 210-0281</p>
                </div>
              </div>

              <div className="info-item">
                <i className="icon">📍</i>
                <div>
                  <h4>Service Area</h4>
                  <p>Utah County & surrounding areas</p>
                </div>
              </div>

              <div className="info-item">
                <i className="icon">⏰</i>
                <div>
                  <h4>Business Hours</h4>
                  <p>Monday - Saturday: 8am - 6pm</p>
                  <p>Sunday: By appointment</p>
                </div>
              </div>
            </div>

            <div className="info-card" style={{ marginTop: '20px' }}>
              <h3>Why Choose Us?</h3>
              <ul className="benefits-list">
                <li>✓ Professional HDR photography</li>
                <li>✓ Fast turnaround available</li>
                <li>✓ Drone/aerial photography</li>
                <li>✓ Virtual staging options</li>
                <li>✓ MLS-ready photos</li>
                <li>✓ Competitive pricing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
